// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import orderBy from 'lodash/orderBy'
import moment from 'moment'
import { ContinuingChecklistInterval } from '../domain/admin/checklist/checklistTypes'

export const EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export type RequiredField<T, K extends keyof T> = T & Required<Pick<T, K>>

export function alphaSort<T>(prop: string | Array<string>, data: Array<T>): Array<T> {
  if (!Array.isArray(prop)) {
    prop = [prop]
  }

  let result = [...data]

  prop.forEach((p) => (result = orderBy(result, [(item) => (item[p] || '').toLowerCase()])))

  return result
}

function dateSort<T>(prop: string, data: Array<T>): Array<T> {
  return orderBy(data, [(item) => moment(item[prop]).unix()])
}

function sort<T>(prop: string, data: Array<T>): Array<T> {
  return orderBy([...data], [(item) => item[prop]])
}

// Remove __typename from mutations recursively
// https://github.com/apollographql/apollo-client/issues/2160
export function removeTypename<T>(value: T): T {
  if (value === null || value === undefined) {
    return value
  } else if (Array.isArray(value)) {
    return value.map((v) => removeTypename(v))
  } else if (typeof value === 'object') {
    const newObj = {}
    Object.entries(value).forEach(([key, v]) => {
      if (key !== '__typename') {
        newObj[key] = removeTypename(v)
      }
    })
    return newObj
  }
  return value
}

type Option = { label: string; value?: string }
export const usersToOptions = (users: Array<Record<string, any>>): Array<Option> =>
  users.map((user) => ({
    label: user.name,
    value: user.id,
  }))

export const getCurrentWorkingDay = () => {
  // first shift for a working day start at 6:00
  return moment().subtract(6, 'hours').format('YYYY-MM-DD')
}

export const markFirstActivityOfEmployees = (employees: array) => {
  // mark first activity of each employee, so that row can be highlighted when rendering the employee table
  const orderedData = orderBy(
    [...employees],
    [
      (item) => {
        return item.user?.name
      },
      (item) => {
        if (!item.startDateTime) {
          return item.startTime
        }
        return new Date(item.startDateTime).getTime()
      },
    ],
    ['asc', 'asc'],
  )
  let currentEmployeeId = ''
  const updatedEmployees = orderedData.map((employee) => {
    let isFirstActivity = false
    const employeeId = employee.userId
    if (employeeId !== currentEmployeeId) {
      currentEmployeeId = employeeId
      isFirstActivity = true
    }
    return {
      ...employee,
      isFirstActivity: isFirstActivity,
    }
  })

  return updatedEmployees
}

export const shouldContinuingItemNameBeHighlighted = (item: any) => {
  switch (item.interval) {
    case ContinuingChecklistInterval.Monthly:
      return getDaysLeftInMonth() <= 7
    case ContinuingChecklistInterval.Weekly:
      return getDaysLeftInWeek() <= 1
    case ContinuingChecklistInterval.Daily:
      return getHoursLeftInDay() <= 3
    default:
      return false
  }
}

const getDaysLeftInMonth = (date: moment.Moment = moment()) => {
  const lastDayOfMonth = moment().endOf('month')
  return lastDayOfMonth.diff(date, 'days')
}

const getDaysLeftInWeek = (date: moment.Moment = moment()) => {
  const lastDayOfWeek = moment().endOf('week')
  return lastDayOfWeek.diff(date, 'days')
}

const getHoursLeftInDay = (date: moment.Moment = moment()) => {
  const endOfDay = moment().endOf('day')
  return endOfDay.diff(date, 'hours')
}

export const downloadExcelFromBase64 = (fileName: string, data: string) => {
  const contentType = EXCEL_MIME_TYPE
  const linkSource = `data:${contentType};base64,${data}`
  const downloadLink = document.createElement('a')

  downloadLink.href = linkSource
  downloadLink.download = fileName
  downloadLink.click()
}

export const getHttpUrl = (url: string) => {
  if (url.includes('http://') || url.includes('https://')) {
    return url
  }
  return `https://${url}`
}

export const isConnectedToInternet = (): boolean => {
  return window !== undefined && window.navigator !== undefined ? window.navigator.onLine : true
}

export const getMonthName = (month: number) => {
  return moment()
    .month(month - 1)
    .format('MMMM')
}

export const toEuroString = (amount?: number | null): string | null => {
  return amount?.toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' }) ?? null
}
